import { apiService } from "../services/apiService";
import { useAuth } from "../contexts/AuthContext";
import * as Flags from "react-flags-select";
import ReactFlagsSelect from "react-flags-select";
import React, { useEffect, useState } from "react";
import "../assets/css/Players.css";

const Players = () => {
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [open, setOpen] = useState(false);
  const [playerEditId, setPlayerEditId] = useState(null);

  const [playerName, setPlayerName] = useState("");
  const [playerNationality, setPlayerNationality] = useState("MA"); // default Morocco
  const [playerPhone, setPlayerPhone] = useState("");
  const [playerEmail, setPlayerEmail] = useState("");

  const { user, isAuthenticated } = useAuth();

  const [isSaving, setIsSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingDefault, setEditingDefault] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [playersPerPage, setPlayersPerPage] = useState(10); // ← user-selectable page size
  const [loading, setLoading] = useState(true);

  // Validation state
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [nameError, setNameError] = useState("");

  // Default players (Player A / Player B) ship with every club:
  // they are always listed, only their flag can be changed, and they cannot be renamed or deleted.
  const isDefaultPlayer = (player) => player?.type === "default";

  const LockIcon = ({ className = "icon icon-sm" }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      strokeWidth="2"
      stroke="currentColor"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-6z" />
      <path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" />
      <path d="M8 11v-4a4 4 0 1 1 8 0v4" />
    </svg>
  );

  const FlagIcon = ({ className = "icon" }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      strokeWidth="2"
      stroke="currentColor"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M5 5a5 5 0 0 1 7 0a5 5 0 0 0 7 0v9a5 5 0 0 1 -7 0a5 5 0 0 0 -7 0v-9z" />
      <path d="M5 21v-7" />
    </svg>
  );

  const getPlayerInitials = (name = "") => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return "?";
    if (/^player\s+[a-z]$/i.test(name.trim())) return words[1].charAt(0).toUpperCase();
    return words
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  };

  const validateEmail = (val) => {
   if (!val.trim()) return ""; // optional
   const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
   return re.test(val) ? "" : "Enter a valid email address.";
 };

 const validatePhone = (val) => {
   if (!val.trim()) return ""; // optional
   const re = /^\+?[0-9\s\-()]{7,20}$/;
   return re.test(val) ? "" : "Enter a valid phone number (7–20 digits).";
 };

  const validateName = (val) => {
    if (!val.trim()) return "Name is required.";
    return "";
  };

  const runValidation = () => {
    const eErr = validateEmail(playerEmail);
    const pErr = validatePhone(playerPhone);
    const nErr = validateName(playerName);
    setEmailError(eErr);
    setPhoneError(pErr);
    setNameError(nErr);
    return !eErr && !pErr && !nErr;
  };

  const getClubPlayers = async (id) => {
    setLoading(true);
    try {
      const data = await apiService.post("/club/players", { id, include_default: true });
      const list = data.players || [];
      setPlayers(list);
      setFilteredPlayers(list);
    } catch (error) {
      console.error("Error fetching players:", error);
      setPlayers([]);
      setFilteredPlayers([]);
    } finally {
      setLoading(false);
    }
  };

  const deletePlayer = async (id) => {
    const data = await apiService.post("/club/player/delete", { id });
    if (data && data.success === false) {
      throw new Error(data.message || "This player cannot be deleted.");
    }
    if (user?.id) {
      await getClubPlayers(user.id);
    }
  };

  const editPlayer = (player) => {
    setEditingDefault(isDefaultPlayer(player));
    setPlayerEditId(player.id);
    setPlayerName(player.name || "");
    setPlayerNationality(player.nationality || "MA");
    setPlayerEmail(player.email || "");
    setPlayerPhone(player.phone || "");
    setEmailError("");
    setPhoneError("");
    setNameError("");
    setOpen(true);
  };

  const resetForm = () => {
    setEditingDefault(false);
    setPlayerEditId(null);
    setPlayerName("");
    setPlayerNationality("MA"); // reset to Morocco by default
    setPlayerEmail("");
    setPlayerPhone("");
    setEmailError("");
    setPhoneError("");
    setNameError("");
  };

  const savePlayer = async () => {
    if (!editingDefault && !runValidation()) return;
    if (isSaving) return;

    setIsSaving(true);
    try {
      // A default player only ever changes its flag; name/email/phone stay untouched.
      const payload = editingDefault
        ? { id_user: user.id, nationality: playerNationality }
        : {
            id_user: user.id,
            name: playerName.trim(),
            nationality: playerNationality,
            email: playerEmail.trim(),
            phone: playerPhone.trim(),
          };

      if (playerEditId === null) {
        await apiService.post("/club/player/store", payload);
      } else {
        await apiService.post("/club/player/update", { ...payload, id: playerEditId });
      }

      await getClubPlayers(user.id);
      resetForm();
      setOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const requestDelete = (player) => {
    if (isDefaultPlayer(player)) return;
    setDeleteError("");
    setDeleteId(player.id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (isDeleting || deleteId == null) return;
    setIsDeleting(true);
    setDeleteError("");
    try {
      await deletePlayer(deleteId);
      setConfirmOpen(false);
      setDeleteId(null);
    } catch (e) {
      console.error(e);
      setDeleteError(e?.message || "Something went wrong, try again later.");
    } finally {
      setIsDeleting(false);
    }
  };

  const getFlagComponent = (countryCode) => {
    if (countryCode && countryCode.length > 1) {
      const formattedCode =
        countryCode.charAt(0).toUpperCase() + countryCode.slice(1).toLowerCase();
      const FlagComponent = Flags[formattedCode];
      return FlagComponent ? <FlagComponent style={{ width: "28px", height: "20px" }} /> : null;
    } else {
      return null;
    }
  };

  // Search
  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (term.trim() === "") {
      setFilteredPlayers(players);
    } else {
      const filtered = players.filter((player) => {
        if (player.deleted_at !== null) return false;

        const searchTermLower = term.toLowerCase();
        const nameMatch = player.name ? player.name.toLowerCase().includes(searchTermLower) : false;
        const emailMatch = player.email ? player.email.toLowerCase().includes(searchTermLower) : false;
        const nationalityMatch = player.nationality
          ? player.nationality.toLowerCase().includes(searchTermLower)
          : false;

        return nameMatch || emailMatch || nationalityMatch;
      });
      setFilteredPlayers(filtered);
    }
    setCurrentPage(1);
  };

  // Counts
  const totalActivePlayers = (players || []).filter((p) => p.deleted_at === null).length;

  // Pagination
  const validPlayers = (filteredPlayers || []).filter((p) => p.deleted_at === null);
  const indexOfLastPlayer = currentPage * playersPerPage;
  const indexOfFirstPlayer = indexOfLastPlayer - playersPerPage;
  const currentPlayers = validPlayers.slice(indexOfFirstPlayer, indexOfLastPlayer);
  const totalPages = Math.ceil(validPlayers.length / playersPerPage) || 1;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Fetch when user becomes available (fixes hard refresh case)
  useEffect(() => {
    if (!user?.id) return;
    getClubPlayers(user.id);
  }, [user?.id]);

  // Disable save until form is valid
 const isFormValid =
   editingDefault || (playerName.trim() && !validateName(playerName));

  return (
    <div className="page-wrapper players-page">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle text-primary">Players Management</div>
              {isAuthenticated() ? <h2 className="page-title">{user ? user.name : ""}</h2> : null}
              <p className="players-page-intro">
                Keep your member roster organized and your two house players ready for walk-in games.
              </p>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button
                  type="button"
                  className="btn btn-primary d-none d-sm-inline-flex club-page-primary-action"
                  onClick={() => {
                    resetForm();
                    setOpen(true);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M12 5l0 14" />
                    <path d="M5 12l14 0" />
                  </svg>
                  Create new player
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="row row-deck row-cards">
            <div className="col-12">
              <div className="card players-card">
                <div className="card-header players-card-header">
                  <div className="players-card-heading">
                    <span className="players-card-kicker">Club roster</span>
                    <div className="players-card-title-row">
                      <h3 className="card-title">Players</h3>
                      <span className="players-count" aria-label={`${totalActivePlayers} active players`}>
                        <strong>{totalActivePlayers}</strong>
                        <span>active</span>
                      </span>
                    </div>
                  </div>

                  <div className="players-toolbar">
                    {/* Page size selector */}
                    <div className="players-page-size">
                      <label htmlFor="players-per-page">Show</label>
                      <select
                        id="players-per-page"
                        className="form-select"
                        value={playersPerPage}
                        onChange={(e) => {
                          setPlayersPerPage(parseInt(e.target.value, 10));
                          setCurrentPage(1);
                        }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                      </select>
                      <span>players</span>
                    </div>

                    {/* Search */}
                    <div className="players-search">
                      <div className="input-icon players-search-field">
                        <span className="input-icon-addon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="icon"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"></path>
                            <path d="M21 21l-6 -6"></path>
                          </svg>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          aria-label="Search players"
                          placeholder="Search by name, email or country"
                          value={searchTerm}
                          onChange={handleSearch}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-vcenter card-table players-table">
                    <thead>
                      <tr>
                        <th scope="col">Player</th>
                        <th scope="col">Nationality</th>
                        <th scope="col">Email</th>
                        <th scope="col">Phone</th>
                        <th scope="col" className="players-actions-heading">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="5" className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : validPlayers.length > 0 ? (
                        currentPlayers.map((player) => {
                          const isDefault = isDefaultPlayer(player);

                          return (
                            <tr
                              key={player.id}
                              className={isDefault ? "players-default-row" : "players-member-row"}
                            >
                                <td>
                                  <div className="players-identity">
                                    <span
                                      className={`players-avatar ${isDefault ? "is-house-player" : ""}`}
                                      aria-hidden="true"
                                    >
                                      {getPlayerInitials(player.name)}
                                    </span>
                                    <span className="players-identity-copy">
                                      <strong>{player.name}</strong>
                                      {isDefault && <small>Always available</small>}
                                    </span>
                                    {isDefault && (
                                      <span
                                        className="players-default-badge"
                                        title="This default player cannot be renamed or deleted."
                                      >
                                        <LockIcon />
                                        Default player
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="players-country">
                                    <span className="players-flag" aria-hidden="true">
                                      {getFlagComponent(player.nationality)}
                                    </span>
                                    <span>{player.nationality || "—"}</span>
                                  </div>
                                </td>
                                <td>
                                  {player.email ? (
                                    <span className="players-contact-value">{player.email}</span>
                                  ) : (
                                    <span
                                      className="players-empty-value"
                                      title={isDefault ? "Not required for house players" : "No email saved"}
                                    >
                                      {isDefault ? "Not required" : "—"}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {player.phone ? (
                                    <span className="players-contact-value">{player.phone}</span>
                                  ) : (
                                    <span
                                      className="players-empty-value"
                                      title={isDefault ? "Not required for house players" : "No phone saved"}
                                    >
                                      {isDefault ? "Not required" : "—"}
                                    </span>
                                  )}
                                </td>
                                <td className="players-actions-cell">
                                  <div className="players-actions">
                                    <button
                                      type="button"
                                      onClick={() => editPlayer(player)}
                                      className={`btn btn-outline-primary players-action-button ${
                                        isDefault ? "players-flag-action" : ""
                                      }`}
                                      title={isDefault ? "Change this house player's flag" : "Edit this player"}
                                    >
                                      {isDefault ? (
                                        <FlagIcon />
                                      ) : (
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="icon"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          strokeWidth="2"
                                          stroke="currentColor"
                                          fill="none"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          aria-hidden="true"
                                        >
                                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                          <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                                          <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                                          <path d="M16 5l3 3" />
                                        </svg>
                                      )}
                                      {isDefault ? "Change flag" : "Edit"}
                                    </button>
                                    {!isDefault && (
                                      <button
                                        type="button"
                                        onClick={() => requestDelete(player)}
                                        className="btn btn-outline-danger players-action-button"
                                        title="Delete this player"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="icon"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          strokeWidth="2"
                                          stroke="currentColor"
                                          fill="none"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          aria-hidden="true"
                                        >
                                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                          <path d="M4 7l16 0" />
                                          <path d="M10 11l0 6" />
                                          <path d="M14 11l0 6" />
                                          <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                                          <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                                        </svg>
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-4">
                            <div className="empty">
                              <div className="empty-img">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="icon icon-tabler icon-tabler-users"
                                  width="50"
                                  height="50"
                                  viewBox="0 0 24 24"
                                  strokeWidth="2"
                                  stroke="currentColor"
                                  fill="none"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                  <path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"></path>
                                  <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
                                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                  <path d="M21 21v-2a4 4 0 0 0 -3 -3.85"></path>
                                </svg>
                              </div>
                              <p className="empty-title">No players found</p>
                              <p className="empty-subtitle text-muted">
                                Try adjusting your search or add a new player
                              </p>
                              <div className="empty-action">
                                <button
                                  className="btn btn-primary"
                                  onClick={() => {
                                    resetForm();
                                    setOpen(true);
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="icon"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2"
                                    stroke="currentColor"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <path d="M12 5l0 14" />
                                    <path d="M5 12l14 0" />
                                  </svg>
                                  Add Player
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer with range, filtered count, pagination */}
                {validPlayers.length > 0 && (
                  <div className="card-footer players-card-footer d-flex flex-wrap gap-2 align-items-center">
                    <p className="m-0 text-muted">
                      Showing <span>{indexOfFirstPlayer + 1}</span> to{" "}
                      <span>{Math.min(indexOfLastPlayer, validPlayers.length)}</span> of{" "}
                      <span>{validPlayers.length}</span> players
                      {searchTerm.trim() && validPlayers.length !== totalActivePlayers ? (
                        <>
                          {" "}
                          <span className="ms-2">(filtered from {totalActivePlayers} total)</span>
                        </>
                      ) : null}
                    </p>
                    {totalPages > 1 && (
                      <ul className="pagination m-0 ms-auto">
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => paginate(currentPage - 1)}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="icon"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              strokeWidth="2"
                              stroke="currentColor"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                              <path d="M15 6l-6 6l6 6"></path>
                            </svg>
                            prev
                          </button>
                        </li>
                        {[...Array(totalPages).keys()].map((number) => (
                          <li
                            key={number + 1}
                            className={`page-item ${currentPage === number + 1 ? "active" : ""}`}
                          >
                            <button className="page-link" onClick={() => paginate(number + 1)}>
                              {number + 1}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                            next
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="icon"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              strokeWidth="2"
                              stroke="currentColor"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                              <path d="M9 6l6 6l-6 6"></path>
                            </svg>
                          </button>
                        </li>
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create / Edit modal */}
      <div
        className={`modal modal-blur fade has-flag-select ${open ? "show" : ""}`}
        id="modal-large"
        tabIndex="-1"
        role="dialog"
        aria-hidden={!open}
        aria-modal={open ? "true" : undefined}
        style={open ? { display: "block" } : { display: "none" }}
      >
        <div
          className={`modal-dialog modal-dialog-centered ${editingDefault ? "" : "modal-lg"}`}
          role="document"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {editingDefault
                  ? "Change house player flag"
                  : playerEditId
                  ? "Edit Player"
                  : "Create New Player"}
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => {
                  setOpen(false);
                }}
              />
            </div>
            <div className="modal-body">
              {editingDefault ? (
                <>
                  {/* Identity is fixed for a default player — show it, don't offer it as a field */}
                  <div className="players-default-modal-summary">
                    <span className="players-avatar is-house-player" aria-hidden="true">
                      {getPlayerInitials(playerName)}
                    </span>
                    <div className="players-default-modal-copy">
                      <span className="players-protected-badge">
                        <LockIcon />
                        Protected house player
                      </span>
                      <strong>{playerName}</strong>
                      <small>The name and contact details stay fixed.</small>
                    </div>
                    <span className="players-flag players-modal-flag" aria-hidden="true">
                      {getFlagComponent(playerNationality)}
                    </span>
                  </div>

                  <div className="players-default-flag-field">
                    <div className="players-default-flag-heading">
                      <div>
                        <label className="form-label">Scoreboard flag</label>
                        <p>Choose the nationality shown beside this player during games.</p>
                      </div>
                      <FlagIcon />
                    </div>
                    <ReactFlagsSelect
                      className="custom-flag-select"
                      searchable
                      selected={playerNationality}
                      onSelect={(code) => setPlayerNationality(code)}
                    />
                  </div>
                </>
              ) : (
              <div className="row">
                {/* Name */}
                <div className="col-lg-12 mb-3">
                  <label className="form-label">Player Name</label>
                  <input
                    type="text"
                    className={`form-control ${nameError ? "is-invalid" : ""}`}
                    placeholder="Enter player name"
                    value={playerName}
                    onChange={(e) => {
                      setPlayerName(e.target.value);
                      if (nameError) setNameError(validateName(e.target.value));
                    }}
                    onBlur={() => setNameError(validateName(playerName))}
                    required
                  />
                  {nameError && <div className="invalid-feedback">{nameError}</div>}
                </div>

                {/* Email */}
                <div className="col-lg-12 mb-3">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className={`form-control ${emailError ? "is-invalid" : ""}`}
                    placeholder="Enter email address"
                    value={playerEmail}
                    onChange={(e) => {
                      setPlayerEmail(e.target.value);
                      if (emailError) setEmailError(validateEmail(e.target.value));
                    }}
                    onBlur={() => setEmailError(validateEmail(playerEmail))}
                  />
                  {emailError && <div className="invalid-feedback">{emailError}</div>}
                </div>

                {/* Nationality */}
                <div className="col-lg-6 mb-3">
                  <label className="form-label">Nationality</label>
                  <ReactFlagsSelect
                    className="custom-flag-select"
                    searchable
                    selected={playerNationality}
                    onSelect={(code) => setPlayerNationality(code)}
                  />
                </div>

                {/* Phone */}
                <div className="col-lg-6 mb-3">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="text"
                    className={`form-control ${phoneError ? "is-invalid" : ""}`}
                    placeholder="Enter phone number"
                    value={playerPhone}
                    onChange={(e) => {
                      setPlayerPhone(e.target.value);
                      if (phoneError) setPhoneError(validatePhone(e.target.value));
                    }}
                    onBlur={() => setPhoneError(validatePhone(playerPhone))}
                  />
                  {phoneError && <div className="invalid-feedback">{phoneError}</div>}
                </div>
              </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-link link-secondary"
                onClick={() => {
                  setOpen(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary ms-auto"
                onClick={savePlayer}
                disabled={isSaving || !isFormValid}
              >
                {isSaving
                  ? "Saving…"
                  : editingDefault
                  ? "Save flag"
                  : playerEditId
                  ? "Update Player"
                  : "Save Player"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirm modal */}
      <div
        className={`modal modal-blur fade ${confirmOpen ? "show" : ""}`}
        tabIndex="-1"
        role="dialog"
        aria-hidden={!confirmOpen}
        style={confirmOpen ? { display: "block" } : { display: "none" }}
      >
        <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-status bg-danger"></div>
            <div className="modal-body text-center py-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mx-auto mb-4 text-danger"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M12 9v4" />
                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
                <path d="M12 16h.01" />
              </svg>
              <h3>Delete player?</h3>
              <div className="text-secondary">This action cannot be undone.</div>
              {deleteError && <div className="text-danger mt-3">{deleteError}</div>}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-link link-secondary me-auto"
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Players;
